import React, { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../services/auth";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "./Logistics.css";
import LogisticsEmployeeForm from "./LogisticsEmployeeForm";
import { useCombinedContext } from "../../context/CombinedContext";
import exportToExcel from "../Excel/ExportToExcel";

const LogisticsEmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const { selectedCompany } = useCombinedContext();
  const companyId = selectedCompany?._id;
  const companyCode = selectedCompany?.company_code;

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/logistics/employee");
      setEmployees(response.data.data || []);
    } catch (error) {
      toast.error("Error fetching logistics employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(0);
  };

  const filteredEmployees = useMemo(() => {
    const text = searchText.trim().toLowerCase();

    return employees.filter((emp) => {
      const companyName = emp.companyId?.company_name || "";
      const matchesSearch =
        !text ||
        emp.name?.toString().toLowerCase().includes(text) ||
        emp.employeeNo?.toString().toLowerCase().includes(text) ||
        companyName.toString().toLowerCase().includes(text);

      const matchesStatus =
        statusFilter === "All" ||
        (statusFilter === "Active" ? !!emp.status : !emp.status);

      return matchesSearch && matchesStatus;
    });
  }, [employees, searchText, statusFilter]);

  const handleDelete = (id) => {
    setDeleteTarget(id);
    setIsDeleteModalOpen(true);
  };

  const handleToggleStatus = async (emp) => {
    try {
      const updatedStatus = !emp.status;

      await axiosInstance.patch(`/logistics/employee/${emp._id}/status`, {
        status: updatedStatus,
      });

      const updatedList = employees.map((e) =>
        e._id === emp._id ? { ...e, status: updatedStatus } : e
      );
      setEmployees(updatedList);

      toast.success(
        updatedStatus
          ? "Employee activated successfully."
          : "Employee deactivated successfully."
      );
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update employee status."
      );
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axiosInstance.delete(`/logistics/employee/${deleteTarget}`);
      setEmployees((prev) => prev.filter((emp) => emp._id !== deleteTarget));
      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
      toast.success("Employee deleted successfully");
    } catch (error) {
      toast.error("Failed to delete employee");
    }
  };

  const handlePageClick = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
  };

  const sortedData = [...filteredEmployees];

  const currentEmployeeItems = sortedData.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );
  const pageCount = Math.ceil(filteredEmployees.length / itemsPerPage);

  const authData =
    JSON.parse(localStorage.getItem("authData")) ||
    JSON.parse(sessionStorage.getItem("authData")) ||
    {};
  const userRole = authData.role;

  const handleExport = () => {
    const tableData = [
      [
        "Sl No",
        "Company Name",
        "Employee Name",
        "Employee No",
        "Status",
        "Location",
      ],
      ...sortedData.map((employee, index) => [
        index + 1,
        employee.companyId?.company_name || companyCode || "N/A",
        employee.name || "N/A",
        employee.employeeNo || "N/A",
        employee.status ? "Active" : "Inactive",
        employee.location || "N/A",
      ]),
    ];
    exportToExcel(tableData, "Logistics-Employee-data.xlsx");
  };

  return (
    <div>
      <ToastContainer />
      <div className="mb-4 headStyle d-flex justify-content-between align-items-center">
        <h4>Logistics Employee</h4>
        <button
          className="button-2"
          onClick={() => {
            setSelectedData(null);
            setShowForm(true);
          }}
          style={{
            borderRadius: "8px",
            whiteSpace: "nowrap",
            padding: "8px 16px",
          }}
        >
          Add Logistics Employee
        </button>
      </div>

      <div className="headStyle mt-4">
        <div className="d-flex mt-2 flex-wrap align-items-center gap-3 justify-content-between">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div>
              <input
                className="form-control"
                type="search"
                placeholder="Search employee / company..."
                style={{
                  maxWidth: "100%",
                  border: "1px solid #1E2A5E",
                  borderRadius: "8px",
                }}
                onChange={handleSearchChange}
              />
            </div>

            <div className="d-flex align-items-center">
              <label className="me-2 pt-1">Count</label>
              <select
                className="form-select no-arrow"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(0);
                }}
                style={{ width: "auto" }}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="15">15</option>
                <option value="20">20</option>
                <option value="25">25</option>
                <option value="30">30</option>
              </select>
            </div>

            <div className="d-flex align-items-center">
              <label className="me-2 pt-1" htmlFor="statusFilter">
                Status Filter
              </label>
              <select
                id="statusFilter"
                className="form-select no-arrow"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(0);
                }}
                style={{ width: "auto" }}
              >
                <option value="All">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          {["admin", "superadmin"].includes(userRole) && (
            <div>
              <button className="button-2 exxportBtn" onClick={handleExport}>
                Export to Excel
              </button>
            </div>
          )}
        </div>

        <div className="my-3 rounded table-responsive-sm" style={{ overflowX: "auto" }}>
          <table className="table table-bordered">
            <thead className="t_head">
              <tr className="text-center align-middle">
                <th>Sl No</th>
                <th>Company Name</th>
                <th>Employee Name</th>
                <th>Employee No</th>
                <th>Status</th>
                <th>Location</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7">
                    <div className="d-flex justify-content-center m-3">
                      <div className="spinner"></div>
                    </div>
                  </td>
                </tr>
              ) : currentEmployeeItems.length === 0 ? (
                <tr>
                  <td colSpan="7">
                    <div className="d-flex justify-content-center m-1">
                      <h6>Data Not Found</h6>
                    </div>
                  </td>
                </tr>
              ) : (
                currentEmployeeItems.map((employee, index) => (
                  <tr key={employee._id || index}>
                    <td>{currentPage * itemsPerPage + index + 1}</td>
                    <td>{employee.companyId?.company_name || "N/A"}</td>
                    <td>{employee.name || "N/A"}</td>
                    <td>{employee.employeeNo || "N/A"}</td>
                    <td>
                      {["admin", "superadmin", "user"].includes(userRole) ? (
                        <div className="form-check form-switch employee-status-switch d-flex justify-content-center align-items-center gap-2 m-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={!!employee.status}
                            onChange={() => handleToggleStatus(employee)}
                            style={{ cursor: "pointer" }}
                            title={
                              employee.status
                                ? "Click to deactivate"
                                : "Click to activate"
                            }
                          />
                          <span
                            className={`badge ${
                              employee.status ? "bg-success" : "bg-danger"
                            }`}
                          >
                            {employee.status ? "Active" : "Inactive"}
                          </span>
                        </div>
                      ) : (
                        <span
                          className={`badge ${
                            employee.status ? "bg-success" : "bg-danger"
                          }`}
                        >
                          {employee.status ? "Active" : "Inactive"}
                        </span>
                      )}
                    </td>
                    <td>{employee.location || "N/A"}</td>
                    <td>
                      <div className="d-flex justify-content-center align-items-center gap-2">
                        <button
                          className="btn btn-outline-success btn-sm"
                          onClick={() => {
                            setSelectedData(employee);
                            setShowForm(true);
                          }}
                        >
                          <i className="bi bi-pencil-square"></i>
                        </button>
                        {["admin", "superadmin"].includes(userRole) && (
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDelete(employee._id)}
                          >
                            <i className="bi bi-trash3"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <ReactPaginate
          breakLabel="..."
          nextLabel="next >"
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel="< previous"
          renderOnZeroPageCount={null}
          containerClassName="pagination justify-content-center pagination-sm"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          activeClassName="active"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          disabledClassName="disabled"
        />

        {showForm && (
          <LogisticsEmployeeForm
            employee={selectedData}
            companyId={companyId}
            selectedCompany={selectedCompany}
            onClose={() => {
              setShowForm(false);
              fetchEmployees();
            }}
          />
        )}

        {isDeleteModalOpen && (
          <div
            className={`modal fade ${isDeleteModalOpen ? "show d-block" : ""}`}
            style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
            tabIndex="-1"
            role="dialog"
            onClick={(e) => {
              if (e.target.classList.contains("modal"))
                setIsDeleteModalOpen(false);
            }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Deletion</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setIsDeleteModalOpen(false)}
                  ></button>
                </div>
                <div className="modal-body modalWhite">
                  Are you sure you want to delete this logistics employee? This action cannot be undone.
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setIsDeleteModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-danger" onClick={confirmDelete}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogisticsEmployeeList;
